import { getDocumentCustomerSchema } from 'src/app/components/document-customer-input/document-customer-schema'
import * as z from 'zod'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useCustomError } from 'src/utils/custom-errors.hook'
import { SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo } from 'react'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'

export default function usePaymentDocumentForm(onSubmit: (data: PaymentDocumentFormSchema, draft: boolean) => Promise<void>, defaultValue: Partial<PaymentDocumentFormSchema>, maxAmount?: number) {
  const { t } = useTranslation()
  const { currency } = useTenant()
  const { formatCurrency } = useFormatCurrency()

  const currencyMaxAmount = useMemo(() => {
    const max = maxAmount ?? Number.MAX_SAFE_INTEGER
    return formatCurrency(max, currency)
  }, [formatCurrency, currency, maxAmount])

  const paymentDocumentFormSchema = getPaymentDocumentFormSchema(t, maxAmount, currencyMaxAmount)
  const customErrorMap = useCustomError()
  z.setErrorMap(customErrorMap)

  const {
    control,
    handleSubmit: rhfHandleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
    setError
  } = useForm<PaymentDocumentFormSchema>({ resolver: zodResolver(paymentDocumentFormSchema), defaultValues: { ...defaultValue } })

  const submitHandler: (draft: boolean) => SubmitHandler<PaymentDocumentFormSchema> = useCallback(
    (draft: boolean) => {
      return async (data: PaymentDocumentFormSchema) => {
        await onSubmit(data, draft)
      }
    }
    , [onSubmit]
  )

  const handleSubmit = useCallback((draft: boolean) => rhfHandleSubmit(submitHandler(draft)), [rhfHandleSubmit, submitHandler])

  return { handleSubmit, control, errors, watch, setValue, getValues, setError }
}


export function getPaymentDocumentFormSchema(t?: TFunction, maxAmount?: number, currencyMaxAmount?: string) {

  return z.object({
    customer: getDocumentCustomerSchema(),
    payment: z.object({
      paymentActiveId: z.string().min(1),
      amount: z.number().min(0).refine((amount) => {
        const max = maxAmount ?? Number.MAX_SAFE_INTEGER
        return amount <= max
      },
        { message: t?.('commons.max_amount', { maxAmount: currencyMaxAmount }) }
      ),
      editable: z.boolean().default(true)
    }),
    documentPayments: getDocumentPaymentSchema().array()
  }).superRefine((value, context) => {
    const totalDocumentsPaidAmount = value.documentPayments.reduce((total, current) => {
      return total + current.amount
    }, 0)
    if (value.payment.amount !== totalDocumentsPaidAmount) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: t?.('pms.accounting.documents.payment_distribution.validation_mismatch'), path: ['payment.amount'] })
    }
  })

}

const paymentDocumentFormSchema = getPaymentDocumentFormSchema()
export type PaymentDocumentFormSchema = z.infer<typeof paymentDocumentFormSchema>


export function getDocumentPaymentSchema() {
  return z
    .object({
      referenceDocumentId: z.string().min(1),
      unpaidAmount: z.number().min(0),
      amount: z.number().min(0),
    }).refine((value) => {
      return value.amount <= value.unpaidAmount
    })
}

const documentPaymentSchema = getDocumentPaymentSchema()
export type DocumentPaymentSchema = z.infer<typeof documentPaymentSchema>